import React from "react";
import { useList } from "react-use";

export function createDeconstructedPromise<T>(): {
    promise: Promise<T>;
    resolve: (args: T) => void;
    reject: () => any;
} {
    let resolve: (args: T) => void = () => {};
    let reject: () => any = () => {};

    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return {
        promise,
        resolve,
        reject,
    };
}

export interface useBatchAsyncWithProgressObjectArgs<I, R> {
    maxSimultanousCalls?: number;
    asyncFn: (item: I) => Promise<R>;
    items: I[];
}

export type useBatchAsyncWithProgressStatus =
    | "success"
    | "error"
    | "loading"
    | "not-executed";

export interface useBatchAsyncWithProgressResult<I, R> {
    result: R[];
    status: useBatchAsyncWithProgressStatus;
    remainingItemsCount: number;
    completedItemsCount: number;
    totalItemCount: number;
    itemsErroredOnAsync: I[];
}

export interface useBatchAsyncWithProgressActions<I, R> {
    reset: () => void;
    execute: () => Promise<void>;
    executionPromise: Promise<{
        success: R[];
        failed: I[];
    }>;
}

export function useBatchAsyncWithProgress<I, R>(
    args: useBatchAsyncWithProgressObjectArgs<I, R>,
): [
    useBatchAsyncWithProgressResult<I, R>,
    useBatchAsyncWithProgressActions<I, R>,
] {
    const { maxSimultanousCalls = 5, asyncFn, items } = args;

    const [remainingItems, remainingItemsActions] = useList<I>([]);
    const [resultOutput, setResultOutput] = React.useState<R[]>([]);
    const [errors, setErrors] = React.useState<I[]>([]);
    const [status, setStatus] =
        React.useState<useBatchAsyncWithProgressStatus>("not-executed");
    const [concurrentCalls, setConcurrentCalls] = React.useState<number>(0);
    const promiseRef = React.useRef(
        createDeconstructedPromise<{
            success: R[];
            failed: I[];
        }>(),
    );

    const executeAsyncFnThenFetchFromQueue = React.useCallback(
        async (item: I) => {
            try {
                const result: R = await asyncFn(item);
                setResultOutput((prev) => [...prev, result]);
            } catch (err) {
                setErrors((prev) => [...prev, item]);
            }
            setConcurrentCalls((prev) => prev - 1);
        },
        [remainingItems, resultOutput, errors],
    );

    React.useEffect(() => {
        if (status === "not-executed") {
            return;
        }

        if (remainingItems.length === 0 && concurrentCalls === 0) {
            promiseRef.current.resolve({
                success: resultOutput,
                failed: errors,
            });

            if (errors.length > 0) {
                setStatus("error");
            } else {
                setStatus("success");
            }
            return;
        }

        if (concurrentCalls < maxSimultanousCalls) {
            const item = remainingItems[0];
            if (item) {
                remainingItemsActions.removeAt(0);
                setConcurrentCalls((prev) => prev + 1);
                void executeAsyncFnThenFetchFromQueue(item);
            }
        }
    }, [remainingItems, concurrentCalls, maxSimultanousCalls]);

    const execute = React.useCallback(async () => {
        setStatus("loading");
        setConcurrentCalls(0);
        remainingItemsActions.set([]);
        setResultOutput([]);

        const initialItems = items.slice(0, maxSimultanousCalls);
        remainingItemsActions.set(
            items.slice(maxSimultanousCalls, items.length),
        );
        initialItems.forEach((i) => {
            void executeAsyncFnThenFetchFromQueue(i);
        });
        setConcurrentCalls(initialItems.length);
    }, [remainingItems, asyncFn]);

    const reset = React.useCallback(() => {
        setStatus("not-executed");
        setConcurrentCalls(0);
        remainingItemsActions.set([]);
        setResultOutput([]);
        promiseRef.current = createDeconstructedPromise();
    }, [status]);

    return [
        {
            result: resultOutput,
            status,
            remainingItemsCount: remainingItems.length,
            completedItemsCount: resultOutput.length,
            totalItemCount: items.length,
            itemsErroredOnAsync: errors,
        },
        {
            reset,
            execute,
            executionPromise: promiseRef.current.promise,
        },
    ];
}
